<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useMainStore }   from '@/store/main';
import { Arrow, Info }    from '../Icons';
import { TimePicker }     from './Index';
import DatePicker         from '@vuepic/vue-datepicker';
import { useApi }         from '@/composables/useApi';

import '@vuepic/vue-datepicker/dist/main.css';

const emit = defineEmits(['setTime']);

const mainStore = useMainStore();

const draft = defineModel();

const { getTimeShedule } = useApi();

const selectedDate = ref(new Date());
const selectedTime = ref(null);
const timeArray    = ref([]);
const isLoading    = ref(true);

const currentDate = computed(() => {
	const dateString = selectedDate.value.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
	return dateString.charAt(0).toUpperCase() + dateString.slice(1);
});

const navigateMonth = increment => {
	const newDate = new Date(selectedDate.value);
	newDate.setMonth(newDate.getMonth() + increment);
	// «назад» с текущего месяца уводил в прошедшую дату, для которой сервер отдаёт ошибку
	selectedDate.value = newDate < new Date() ? new Date() : newDate;
};

// toISOString() переводит в UTC: полночь по Москве превращается в предыдущий день
const toLocalISODate = d =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// слоты по дате живут, пока открыт календарь: повторный клик по дню — без запроса
const slotsCache = {};

const fetchSlots = date => {
	const serviceIds = draft.value.services.map(el => el.id);

	slotsCache[date] ??= getTimeShedule({ id: draft.value.master.id, service_ids: serviceIds, date })
		.then(slots => {
			if (!slots) delete slotsCache[date];
			return slots || [];
		});

	return slotsCache[date];
};

const getTimesArray = async () => {
	const date = toLocalISODate(selectedDate.value);

	isLoading.value    = true;
	selectedTime.value = null;
	timeArray.value    = [];

	const slots = await fetchSlots(date);

	// пока ждали, могли кликнуть другой день — старый ответ не показываем
	if (date != toLocalISODate(selectedDate.value))
		return;

	timeArray.value = slots;
	isLoading.value = false;
}

// ponytail: 6 фоновых запросов на открытие календаря; при росте нагрузки — один эндпоинт на диапазон дат
const prefetchWeek = () => {
	for (let i = 1; i <= 6; i++) {
		const day = new Date(selectedDate.value);
		day.setDate(day.getDate() + i);
		fetchSlots(toLocalISODate(day));
	}
};

const setDraftTime = () => {
	draft.date = toLocalISODate(selectedDate.value);
	draft.time = selectedTime.value;

	emit('setTime', {
		date: draft.date,
		time: draft.time
	});
}

watch(selectedDate, getTimesArray);

onMounted(() => getTimesArray().then(prefetchWeek));
</script>

<template>
	<div class="data-picker-wr">
		<div class="dropdown-content-wr">
			<div class="dropdown-content__header">
				Выберите дату и время
			</div>
			<div class="dropdown-content__content">
			<DatePicker
				v-model="selectedDate"
				inline
				auto-apply
				:day-names="['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']"
				:enable-time-picker="false"
				:month-change-on-scroll="false"
				:min-date="new Date()"
			>
				<template #month-year>
					<div class="custom-month-year-component">
						<button 
							class="nav-button prev-month" 
							@click="navigateMonth(-1)"
							aria-label="Предыдущий месяц"
						>
							<Arrow />
						</button>
						<span>{{ currentDate }}</span>
						<button 
							class="nav-button next-month" 
							@click="navigateMonth(1)"
							aria-label="Следующий месяц"
						>
							<Arrow />
						</button>
					</div>
				</template>
			</DatePicker>
			<TimePicker
				:timeArray
				v-model:selectedTime="selectedTime"
				v-if="timeArray.length"
			/>
			<div class="time-picker__no-time" v-else-if="!isLoading">
				<Info />
				<span>На выбранный день у мастера нет доступного времени</span>
			</div>
			<button
				class="time-picker__apply-btn"
				:disabled="!selectedTime"
				@click="setDraftTime"
			>
				Подтвердить выбор
			</button>
			</div>
		</div>
	</div>
</template>

<style lang='scss'>
.data-picker-wr {
	.dropdown-content__content {
		max-height: unset;
		padding: 20px 16px;
	}
}

.custom-month-year-component {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;

	.nav-button {
		width: 20px;
		height: 20px;
		display: grid;
		place-items: center;

		svg {
			width: 16px;
			height: 16px;
		}

		&.prev-month { transform: rotate(90deg) }

		&.next-month { transform: rotate(-90deg) }

		svg { color: #000 }
	}
}

.dp__main > div:nth-child(2) {
	width: 100%;
}

.dp__menu_inner { padding: 0 }

.dp__instance_calendar,
.dp__today,
.dp__menu { border: none }

.dp__calendar_header { margin-top: 30px }

.dp__calendar_header .dp__calendar_header_item {
	font-family: 'Raleway';
	font-size: 14px;
	font-weight: 400;
	color: #C4C4C4;
}

.dp__calendar_item
{
	font-family: 'Raleway';
	font-weight: 500;
}

.dp__calendar_header_separator { display: none }

.dp__cell_offset { color: #C4C4C4 }

.dp__cell_inner {
	font-family: 'Inter';
	font-size: 14px;
}

.dp__cell_inner:not(.dp__cell_offset) {
	background-color: #F9F9F9;
	border-radius: 8px;
}

.dp__active_date {
	background-color: $yellow !important;
	font-family: 'Raleway';
	font-weight: 500;
}

.time-picker__no-time {
	margin-top: 12px;
	padding: 16px;
	display: flex;
	font-size: 14px;
	gap: 8px;
	border-radius: 8px;
	background-color: #F5F5F5;
}
</style>